import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CloseIcon } from '../icons/CloseIcon';

type QuickReply = {
  label: string;
  next: string;
};

type ChatTurn = {
  assistant: ReactNode;
  media?: { videoUrl?: string; posterUrl?: string };
  quickReplies: QuickReply[];
};

const END = '__end';

const SCRIPT: Record<string, ChatTurn> = {
  welcome: {
    assistant: (
      <>
        Hey 👋 I&apos;ll walk you through setting up <strong>Google Postmaster Tools</strong>. It
        takes about 5 minutes. Ready?
      </>
    ),
    quickReplies: [{ label: "Let's go", next: 'signin' }],
  },

  signin: {
    assistant: (
      <>
        First — open{' '}
        <a
          className="underline"
          href="https://postmaster.google.com"
          target="_blank"
          rel="noreferrer"
        >
          Google Postmaster Tools
        </a>{' '}
        and sign in with your Google account.
      </>
    ),
    media: { posterUrl: '/step1/1.gif' },
    quickReplies: [
      { label: "I'm signed in", next: 'add-domain' },
      { label: 'Help me', next: 'add-domain' },
    ],
  },

  'add-domain': {
    assistant: 'Click Add domain at the top and enter your sending domain (e.g. yourbrand.com).',
    media: { posterUrl: '/step1/2.gif' },
    quickReplies: [{ label: 'Done', next: 'txt-record' }],
  },

  'txt-record': {
    assistant: "Google just generated a TXT record — copy it. You'll paste it into your DNS next.",
    media: { posterUrl: '/step1/3.gif' },
    quickReplies: [{ label: 'Copied', next: 'dns-choice' }],
  },

  'dns-choice': {
    assistant: 'Now add it to your DNS. Want me to do it automatically with Entri, or manually?',
    quickReplies: [
      { label: 'Automatic', next: 'auto-confirm' },
      { label: 'Manual', next: 'manual-intro' },
    ],
  },

  'auto-confirm': {
    assistant:
      "Great — Entri will detect your DNS provider and add the record automatically. Click Continue when you're ready.",
    quickReplies: [{ label: 'Continue', next: 'verify' }],
  },

  // --- Manual branch ---
  'manual-intro': {
    assistant: (
      <>
        Manual it is. First — log in to your domain provider (<strong>Squarespace</strong>,{' '}
        <strong>GoDaddy</strong>, <strong>Namecheap</strong>, <strong>Cloudflare</strong>, etc.).
      </>
    ),
    quickReplies: [
      { label: "I'm in", next: 'manual-find-dns' },
      { label: "I don't know my provider", next: 'manual-find-provider' },
    ],
  },

  'manual-find-provider': {
    assistant: (
      <>
        No worries. Run{' '}
        <a
          className="underline"
          href="https://www.whois.com/whois"
          target="_blank"
          rel="noreferrer"
        >
          a WHOIS lookup
        </a>{' '}
        on your domain — the &quot;Registrar&quot; field tells you who manages it.
      </>
    ),
    quickReplies: [{ label: 'Found it', next: 'manual-find-dns' }],
  },

  'manual-find-dns': {
    assistant: (
      <>
        Look for <strong>DNS</strong>, <strong>DNS settings</strong>, or{' '}
        <strong>Advanced DNS</strong>. It&apos;s usually inside the domain&apos;s settings page.
      </>
    ),
    quickReplies: [
      { label: 'Found DNS', next: 'manual-add-record' },
      { label: "Can't find it", next: 'manual-dns-help' },
    ],
  },

  'manual-dns-help': {
    assistant: (
      <>
        Some providers hide it under <em>Domain → Manage → DNS</em>, others call it{' '}
        <em>Nameservers</em>. If you only see nameservers pointing somewhere else (like Cloudflare),
        you&apos;ll need to add the TXT there instead.
      </>
    ),
    quickReplies: [{ label: 'OK, found it', next: 'manual-add-record' }],
  },

  'manual-add-record': {
    assistant: (
      <>
        Create a new record with these values:
        <span className="mt-2 block rounded-[8px] bg-[rgba(60,50,47,0.06)] px-3 py-2 font-mono text-[12px] leading-[18px]">
          Type: <strong>TXT</strong>
          <br />
          Host / Name: <strong>@</strong> (or your root domain)
          <br />
          Value: <em>the TXT you copied from Google</em>
          <br />
          TTL: <strong>3600</strong> (or default)
        </span>
      </>
    ),
    quickReplies: [
      { label: 'Added', next: 'manual-save' },
      { label: 'What goes in Host?', next: 'manual-host-help' },
    ],
  },

  'manual-host-help': {
    assistant: (
      <>
        <strong>Host</strong> says which subdomain the record applies to. For Postmaster you want it
        at your root, so use <code className="rounded bg-[rgba(60,50,47,0.06)] px-1">@</code> — if
        your provider doesn&apos;t accept{' '}
        <code className="rounded bg-[rgba(60,50,47,0.06)] px-1">@</code>, type your full domain
        (e.g. <code className="rounded bg-[rgba(60,50,47,0.06)] px-1">yourbrand.com</code>).
      </>
    ),
    quickReplies: [{ label: 'Got it', next: 'manual-add-record' }],
  },

  'manual-save': {
    assistant:
      'Save the record. DNS changes can take anywhere from a few minutes to an hour to propagate worldwide.',
    quickReplies: [
      { label: 'Saved ✅', next: 'manual-verify-tip' },
      { label: 'Error saving', next: 'manual-save-help' },
    ],
  },

  'manual-save-help': {
    assistant: (
      <>
        Common gotchas: duplicate TXT for the same host, value wrapped in extra quotes, or a
        trailing space. Re-check the value matches what Google gave you exactly.
      </>
    ),
    quickReplies: [{ label: 'Fixed', next: 'manual-verify-tip' }],
  },

  'manual-verify-tip': {
    assistant: (
      <>
        Tip — you can check propagation with{' '}
        <a
          className="underline"
          href="https://mxtoolbox.com/TXTLookup.aspx"
          target="_blank"
          rel="noreferrer"
        >
          MXToolbox TXT lookup
        </a>
        . Once it shows your record, you&apos;re good to verify.
      </>
    ),
    quickReplies: [{ label: 'Ready to verify', next: 'verify' }],
  },

  // --- Verify + finish ---
  verify: {
    assistant: 'Back to Postmaster — find your domain and click Verify. The check is instant.',
    quickReplies: [
      { label: 'Verified ✅', next: 'authorize' },
      { label: 'Failed', next: 'verify-fail' },
    ],
  },

  'verify-fail': {
    assistant:
      "DNS probably hasn't propagated yet. Wait 5–10 minutes and try again — no need to redo anything.",
    quickReplies: [{ label: 'Try again', next: 'verify' }],
  },

  authorize: {
    assistant: 'Last step — authorize Flodesk to read your reputation data from Google.',
    quickReplies: [{ label: 'Authorize', next: 'done' }],
  },

  done: {
    assistant: (
      <>🎉 You&apos;re all set! Google Postmaster will start showing data within 24 hours.</>
    ),
    quickReplies: [{ label: 'Finish', next: END }],
  },
};

type Message =
  | { role: 'assistant'; content: ReactNode; media?: { videoUrl?: string; posterUrl?: string } }
  | { role: 'user'; content: string };

type ChatViewProps = {
  onExit?: () => void;
  onComplete?: () => void;
};

export const ChatView = ({ onExit, onComplete }: ChatViewProps) => {
  const [turnId, setTurnId] = useState<string>('welcome');
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: SCRIPT.welcome.assistant,
      media: SCRIPT.welcome.media,
    },
  ]);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, typing]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onExit?.();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExit]);

  const currentTurn = SCRIPT[turnId];

  const handleReply = (reply: QuickReply) => {
    setMessages(prev => [...prev, { role: 'user', content: reply.label }]);

    if (reply.next === END) {
      setTimeout(() => onComplete?.(), 500);
      return;
    }

    const nextTurn = SCRIPT[reply.next];
    if (!nextTurn) return;

    setTyping(true);
    setTimeout(() => {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: nextTurn.assistant, media: nextTurn.media },
      ]);
      setTyping(false);
      setTurnId(reply.next);
    }, 800);
  };

  return (
    <main className="absolute inset-0 flex flex-col bg-white">
      <header className="flex h-[64px] shrink-0 items-center justify-between border-b border-[rgba(60,50,47,0.08)] px-l2">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-full bg-content text-[13px] font-medium text-white">
            F
          </div>
          <div className="flex flex-col leading-tight">
            <p className="text-[14px] font-medium text-content">Flodesk Assistant</p>
            <p className="text-[12px] text-content-muted">Setting up Google Postmaster</p>
          </div>
        </div>
        <button
          type="button"
          aria-label="Close chat"
          onClick={onExit}
          className="flex size-9 items-center justify-center rounded-[3px] text-content-muted transition-colors hover:bg-black/5 hover:text-content"
        >
          <CloseIcon />
        </button>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-m px-l2 py-[40px]">
          {messages.map((msg, i) => (
            <MessageBubble key={i} message={msg} />
          ))}
          {typing && <TypingBubble />}
        </div>
      </div>

      <div className="shrink-0 border-t border-[rgba(60,50,47,0.08)] bg-white">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-3 px-l2 py-l">
          {!typing && currentTurn && (
            <div className="flex flex-wrap justify-end gap-2">
              {currentTurn.quickReplies.map(reply => (
                <button
                  key={reply.label}
                  type="button"
                  onClick={() => handleReply(reply)}
                  className="rounded-full border border-[rgba(60,50,47,0.18)] bg-white px-l py-2 text-[13px] font-medium text-content transition-colors hover:bg-black/5"
                >
                  {reply.label}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2 rounded-full border border-[rgba(60,50,47,0.12)] bg-white px-l py-3">
            <input
              type="text"
              placeholder="Ask me anything..."
              readOnly
              className="flex-1 bg-transparent text-[14px] text-content placeholder:text-content-muted focus:outline-none"
            />
            <button
              type="button"
              aria-label="Send"
              disabled
              className="flex size-8 items-center justify-center rounded-full bg-[rgba(60,50,47,0.08)] text-content-muted"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M7 11V3M7 3L3 7M7 3L11 7"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

const MessageBubble = ({ message }: { message: Message }) => {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[460px] rounded-[18px] rounded-br-[6px] bg-content px-l py-3 text-[14px] font-normal leading-[20px] text-white">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="flex max-w-[520px] flex-col gap-2">
      <div className="rounded-[18px] rounded-bl-[6px] bg-[#f3f2f1] px-l py-3 text-[14px] font-normal leading-[20px] text-content">
        {message.content}
      </div>
      {message.media && (
        <div className="overflow-hidden rounded-[14px]">
          {message.media.posterUrl ? (
            <img
              src={message.media.posterUrl}
              alt=""
              className="block w-full max-w-[420px] object-cover"
            />
          ) : (
            <div
              className="h-[200px] w-full max-w-[420px]"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, rgba(60,50,47,0.15) 0%, rgba(255,255,255,0.35) 100%)',
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

const TypingBubble = () => (
  <div className="flex">
    <div className="flex items-center gap-1 rounded-[18px] rounded-bl-[6px] bg-[#f3f2f1] px-l py-3">
      <span className="size-[6px] animate-bounce rounded-full bg-content-muted [animation-delay:-0.3s]" />
      <span className="size-[6px] animate-bounce rounded-full bg-content-muted [animation-delay:-0.15s]" />
      <span className="size-[6px] animate-bounce rounded-full bg-content-muted" />
    </div>
  </div>
);
